import { useCallback, useEffect, useRef, useState } from 'react'

export interface TabVisibilityState {
  isVisible: boolean
  isHidden: boolean
  lastVisibleTime: number
  lastHiddenTime: number
  timeHidden: number
}

export interface TabActivityOptions {
  onVisibilityChange?: (state: TabVisibilityState) => void
  onBecomeVisible?: () => void
  onBecomeHidden?: () => void
  trackTimeHidden?: boolean
}

/**
 * Hook to track tab visibility changes and provide activity state
 * Useful for notifications when user switches between tabs
 */
export const useTabVisibility = (options: TabActivityOptions = {}) => {
  const { onVisibilityChange, onBecomeVisible, onBecomeHidden, trackTimeHidden = true } = options

  const [isVisible, setIsVisible] = useState<boolean>(() => {
    if (typeof document !== 'undefined') {
      return !document.hidden
    }
    return true
  })

  const lastVisibleTimeRef = useRef<number>(Date.now())
  const lastHiddenTimeRef = useRef<number>(0)
  const timeHiddenRef = useRef<number>(0)

  const getVisibilityState = useCallback((): TabVisibilityState => {
    return {
      isVisible,
      isHidden: !isVisible,
      lastVisibleTime: lastVisibleTimeRef.current,
      lastHiddenTime: lastHiddenTimeRef.current,
      timeHidden: timeHiddenRef.current,
    }
  }, [isVisible])

  const handleVisibilityChange = useCallback(() => {
    const isCurrentlyVisible = !document.hidden
    const now = Date.now()

    setIsVisible(isCurrentlyVisible)

    if (isCurrentlyVisible) {
      // Tab became visible
      lastVisibleTimeRef.current = now

      if (trackTimeHidden && lastHiddenTimeRef.current > 0) {
        timeHiddenRef.current = now - lastHiddenTimeRef.current
      }

      onBecomeVisible?.()
    } else {
      // Tab became hidden
      lastHiddenTimeRef.current = now
      onBecomeHidden?.()
    }

    const newState = {
      isVisible: isCurrentlyVisible,
      isHidden: !isCurrentlyVisible,
      lastVisibleTime: lastVisibleTimeRef.current,
      lastHiddenTime: lastHiddenTimeRef.current,
      timeHidden: timeHiddenRef.current,
    }

    onVisibilityChange?.(newState)
  }, [onVisibilityChange, onBecomeVisible, onBecomeHidden, trackTimeHidden])

  useEffect(() => {
    // Check if browser supports Page Visibility API
    if (typeof document === 'undefined' || typeof document.addEventListener !== 'function') {
      return
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [handleVisibilityChange])

  return {
    isVisible,
    isHidden: !isVisible,
    getVisibilityState,
    timeHidden: timeHiddenRef.current,
  }
}

export default useTabVisibility
